import React from 'react';
import MainPrintLogo from './MainPrintLogo';
import { printPalette, printTypography, printManifest, printInvoice } from './printPalette';
import { basePrintContainerStyle } from './printStyles';
import { FeeType, AdditionalFeeValue } from '../types/entities';

interface RelatedCarItem {
  chassisNo: string;
  make: string;
  model: string;
  relationshipType: string;
  additionalFees: Record<string, AdditionalFeeValue>;
}

interface InvoiceCarItem {
  position: number;
  chassisNo: string;
  make: string;
  model: string;
  port: string;
  length: number | null;
  carType: string;
  freight: number;
  collect: number;
  additionalFees: Record<string, AdditionalFeeValue>;
  relatedCars?: RelatedCarItem[];
}

interface InvoiceProps {
  companyName?: string;
  companyAddress?: string;
  invoiceNumber: string;
  date: string;
  voyageNo: string;
  vesselName: string;
  shipperCode: string;
  shipperName: string;
  shipperAddress?: string;
  portFilter?: string;
  pol?: string;
  cars: InvoiceCarItem[];
  includeRelated?: boolean;
}

const Invoice: React.FC<InvoiceProps> = ({
  companyName = 'CLAPOTIS CARRIERS LTD',
  companyAddress = 'Trust Company Complex, Ajeltake Road, Majuro, Marshall Islands',
  invoiceNumber,
  date,
  voyageNo,
  vesselName,
  shipperCode,
  shipperName,
  shipperAddress,
  portFilter,
  pol,
  cars,
  includeRelated = true,
}) => {
  // Calculate totals including additional fees from main cars and related cars
  // 'F' = Freight only, 'C' = Collect only, 'B' = Both (added to both columns)
  const totalFreight = cars.reduce((sum, c) => {
    const baseFreight = c.freight;
    const freightFees = Object.values(c.additionalFees || {})
      .filter(fee => fee.type === 'F' || fee.type === 'B')
      .reduce((feeSum, fee) => feeSum + fee.amount, 0);
    // Include related car fees
    const relatedFreightFees = (c.relatedCars || []).reduce((rcSum, rc) => {
      return rcSum + Object.values(rc.additionalFees || {})
        .filter(fee => fee.type === 'F' || fee.type === 'B')
        .reduce((feeSum, fee) => feeSum + fee.amount, 0);
    }, 0);
    return sum + baseFreight + freightFees + relatedFreightFees;
  }, 0);

  const totalCollect = cars.reduce((sum, c) => {
    const baseCollect = c.collect;
    const collectFees = Object.values(c.additionalFees || {})
      .filter(fee => fee.type === 'C' || fee.type === 'B')
      .reduce((feeSum, fee) => feeSum + fee.amount, 0);
    // Include related car fees
    const relatedCollectFees = (c.relatedCars || []).reduce((rcSum, rc) => {
      return rcSum + Object.values(rc.additionalFees || {})
        .filter(fee => fee.type === 'C' || fee.type === 'B')
        .reduce((feeSum, fee) => feeSum + fee.amount, 0);
    }, 0);
    return sum + baseCollect + collectFees + relatedCollectFees;
  }, 0);

  const invoiceAmount = Math.abs(totalCollect - totalFreight);

  // Calculate total vehicle count - only main cars (not related)
  const totalVehicleCount = cars.length;

  const formatCurrency = (value: number) => {
    if (value === 0) return '-';
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Helper to get additional fees as an array for display, grouped by type
  const getAdditionalFeesArray = (fees: Record<string, AdditionalFeeValue>): { name: string; value: number; type: FeeType }[] => {
    return Object.entries(fees).map(([name, fee]) => ({ name, value: fee.amount, type: fee.type }));
  };

  return (
    <>
      <style>{invoicePrintStyles}</style>
      <div className="invoice" style={styles.container}>
        {/* Header */}
        <MainPrintLogo companyName={companyName} companyAddress={companyAddress} />

        {/* Title */}
        <div style={styles.titleRow}>
          <h2 style={styles.title}>INVOICE</h2>
          <span style={styles.invoiceNumberValue}>{invoiceNumber}</span>
        </div>

        {/* Info Section */}
        <div style={styles.infoSection}>
          <div style={styles.leftInfo}>
            <p><strong>Date:</strong> {date}</p>
            {pol && <p><strong>POL:</strong> {pol}</p>}
            {portFilter && <p><strong>Port(s):</strong> {portFilter}</p>}
          </div>
          <div style={styles.rightInfo}>
            <p><strong>Voyage:</strong> {voyageNo}</p>
            <p><strong>Vessel:</strong> {vesselName}</p>
          </div>
        </div>

        {/* Shipper Info */}
        <div style={styles.shipperBox}>
          <p style={{ fontSize: '10px', color: printPalette.textMuted, margin: '0 0 4px 0' }}>Bill To:</p>
          <p style={{ fontSize: '11px', fontWeight: 'bold', color: printPalette.primaryDark, margin: 0 }}>{shipperCode}</p>
          <p style={{ fontSize: '11px', color: printPalette.text, margin: '2px 0 0 0' }}>{shipperName}</p>
          {shipperAddress && shipperAddress.split('\n').map((line, i) => (
            <p key={i} style={{ fontSize: '10px', color: printPalette.textMuted, margin: '2px 0 0 0' }}>{line}</p>
          ))}
        </div>

        {/* Table */}
        <table className="invoice-table" style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: '5%', textAlign: 'center' }}>No.</th>
              <th style={{ ...styles.th, width: '14%' }}>POD</th>
              <th style={{ ...styles.th, width: '26%' }}>Make/Model</th>
              <th style={{ ...styles.th, width: '26%' }}>Chassis No.</th>
              <th style={{ ...styles.th, width: '8.7%', textAlign: 'right' }}>Length</th>
              <th style={{ ...styles.th, width: '8.7%', textAlign: 'right' }}>Freight</th>
              <th style={{ ...styles.th, width: '8.7%', textAlign: 'right' }}>Collect</th>
            </tr>
          </thead>
          <tbody>
            {cars.map((car, idx) => {
              const additionalFeesArr = getAdditionalFeesArray(car.additionalFees);
              const hasAdditionalFees = additionalFeesArr.length > 0;
              const hasRelatedCars = car.relatedCars && car.relatedCars.length > 0;

              return (
                <React.Fragment key={idx}>
                  {/* Main row */}
                  <tr className="table-row">
                    <td style={{ ...styles.td, textAlign: 'center' }}>{car.position}</td>
                    <td style={styles.td}>{car.port}</td>
                    <td style={styles.td}>{car.make} {car.model}</td>
                    <td style={styles.td}>{car.chassisNo}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      {car.carType === 'L' && car.length ? car.length.toFixed(2) : '-'}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{formatCurrency(car.freight)}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{formatCurrency(car.collect)}</td>
                  </tr>
                  {/* Additional fees rows */}
                  {hasAdditionalFees && additionalFeesArr.map((fee, feeIdx) => (
                    <tr key={`${idx}-fee-${feeIdx}`} className="table-row" style={{ backgroundColor: printPalette.rowAltBg }}>
                      <td style={{ ...styles.td, textAlign: 'center', borderTop: 'none', paddingTop: '2px' }}></td>
                      <td style={{ ...styles.td, borderTop: 'none', paddingTop: '2px' }}></td>
                      <td colSpan={3} style={{ ...styles.td, borderTop: 'none', paddingTop: '2px', paddingLeft: '8px', fontStyle: 'italic', color: printPalette.text }}>
                        + {fee.name}
                      </td>
                      <td style={{ ...styles.td, textAlign: 'right', borderTop: 'none', paddingTop: '2px', fontStyle: 'italic', color: printPalette.text }}>
                        {(fee.type === 'F' || fee.type === 'B') ? formatCurrency(fee.value) : '-'}
                      </td>
                      <td style={{ ...styles.td, textAlign: 'right', borderTop: 'none', paddingTop: '2px', fontStyle: 'italic', color: printPalette.text }}>
                        {(fee.type === 'C' || fee.type === 'B') ? formatCurrency(fee.value) : '-'}
                      </td>
                    </tr>
                  ))}
                  {/* Related cars rows */}
                  {includeRelated && hasRelatedCars && car.relatedCars!.map((rc, rcIdx) => {
                    const rcFeesArr = getAdditionalFeesArray(rc.additionalFees);
                    return (
                      <React.Fragment key={`${idx}-rc-${rcIdx}`}>
                        <tr className="table-row" style={{ backgroundColor: printPalette.rowAltBg }}>
                          <td style={{ ...styles.td, textAlign: 'center', borderTop: 'none' }}></td>
                          <td style={{ ...styles.td, borderTop: 'none' }}></td>
                          <td style={{ ...styles.td, borderTop: 'none', paddingLeft: '12px', fontStyle: 'italic' }}>
                            ↳ {rc.make} {rc.model} <span style={{ color: printPalette.textMuted, fontSize: '9px' }}>({rc.relationshipType})</span>
                          </td>
                          <td style={{ ...styles.td, borderTop: 'none', fontStyle: 'italic' }}>{rc.chassisNo}</td>
                          <td style={{ ...styles.td, textAlign: 'right', borderTop: 'none' }}>-</td>
                          <td style={{ ...styles.td, textAlign: 'right', borderTop: 'none' }}>-</td>
                          <td style={{ ...styles.td, textAlign: 'right', borderTop: 'none' }}>-</td>
                        </tr>
                        {/* Related car additional fees */}
                        {rcFeesArr.map((fee, feeIdx) => (
                          <tr key={`${idx}-rc-${rcIdx}-fee-${feeIdx}`} className="table-row" style={{ backgroundColor: printPalette.rowAltBg }}>
                            <td style={{ ...styles.td, textAlign: 'center', borderTop: 'none', paddingTop: '2px' }}></td>
                            <td style={{ ...styles.td, borderTop: 'none', paddingTop: '2px' }}></td>
                            <td colSpan={3} style={{ ...styles.td, borderTop: 'none', paddingTop: '2px', paddingLeft: '20px', fontStyle: 'italic', color: printPalette.text }}>
                              + {fee.name}
                            </td>
                            <td style={{ ...styles.td, textAlign: 'right', borderTop: 'none', paddingTop: '2px', fontStyle: 'italic', color: printPalette.text }}>
                              {(fee.type === 'F' || fee.type === 'B') ? formatCurrency(fee.value) : '-'}
                            </td>
                            <td style={{ ...styles.td, textAlign: 'right', borderTop: 'none', paddingTop: '2px', fontStyle: 'italic', color: printPalette.text }}>
                              {(fee.type === 'C' || fee.type === 'B') ? formatCurrency(fee.value) : '-'}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {/* Totals Summary */}
        <div style={styles.totalsSection}>
          <div style={styles.totalsSummary}>
            <div style={styles.totalsRow}>
              <span style={styles.totalsLabel}>Total ({totalVehicleCount} vehicle{totalVehicleCount !== 1 ? 's' : ''})</span>
            </div>
            <div style={styles.totalsRow}>
              <span style={styles.totalsLabel}>Total Freight:</span>
              <span style={styles.totalsValue}>{formatCurrency(totalFreight)} EUR</span>
            </div>
            <div style={styles.totalsRow}>
              <span style={styles.totalsLabel}>Total Collect:</span>
              <span style={styles.totalsValue}>{formatCurrency(totalCollect)} EUR</span>
            </div>
            <div style={styles.totalsRowHighlight}>
              <span style={styles.totalsLabel}>Invoice Amount:</span>
              <span style={styles.invoiceAmountValue}>{formatCurrency(invoiceAmount)} EUR</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const invoicePrintStyles = `
  @page {
    size: A4 portrait;
    margin: 10mm;
  }

  @media print {
    body {
      background: #ffffff;
      margin: 0;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .invoice-table { page-break-inside: auto; }
    .invoice-table thead { display: table-header-group; }
    .invoice-table tbody { display: table-row-group; }
    .table-row { page-break-inside: avoid; page-break-after: auto; }
  }
`;

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    ...basePrintContainerStyle,
    padding: printManifest.containerPadding,
    fontSize: `${printTypography.baseSize}px`,
    border: printInvoice.containerBorder,
    lineHeight: 1.4,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '12px',
    marginBottom: '20px',
  },
  title: {
    fontSize: `${printTypography.titleSize}px`,
    fontWeight: 'bold',
    margin: 0,
    color: printPalette.primaryDark,
    textAlign: 'left',
  },
  infoSection: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '20px',
    fontSize: `${printTypography.infoSize}px`,
    color: printPalette.text,
  },
  leftInfo: {
    textAlign: 'left',
  },
  rightInfo: {
    textAlign: 'right',
  },
  invoiceNumberValue: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: printPalette.primaryDark,
  },
  shipperBox: {
    marginBottom: '20px',
    padding: '12px',
    backgroundColor: printPalette.headerBg,
    border: `1px solid ${printPalette.borderLight}`,
    borderRadius: '4px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: `${printTypography.tableSize}px`,
    color: printPalette.text,
    tableLayout: 'fixed',
  },
  th: {
    border: `${printManifest.cellBorder} solid ${printPalette.primaryDark}`,
    borderTop: `${printManifest.headerBorder} solid ${printPalette.primaryDark}`,
    borderBottom: `${printManifest.headerBorder} solid ${printPalette.primaryDark}`,
    padding: `${printManifest.headerPadding} 6px`,
    textAlign: 'left',
    fontWeight: 'bold',
    color: printPalette.primaryDark,
    backgroundColor: printPalette.headerBg,
  },
  td: {
    padding: printManifest.cellPadding,
    border: `${printManifest.cellBorder} solid ${printPalette.borderLight}`,
    color: printPalette.text,
    verticalAlign: 'top',
  },
  totalsSection: {
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  totalsSummary: {
    minWidth: '280px',
    border: `1px solid ${printPalette.borderLight}`,
    borderRadius: '4px',
    backgroundColor: printPalette.headerBg,
    padding: '12px 16px',
  },
  totalsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 0',
    fontSize: '11px',
    color: printPalette.text,
  },
  totalsRowHighlight: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0 4px 0',
    marginTop: '8px',
    borderTop: `2px solid ${printPalette.primaryDark}`,
  },
  totalsLabel: {
    fontWeight: 500,
  },
  totalsValue: {
    fontWeight: 500,
    textAlign: 'right',
  },
  invoiceAmountValue: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: printPalette.primaryDark,
  },
};

export default Invoice;

